import React from 'react';
import { Lock, Sparkles, Target, Megaphone, Coins } from 'lucide-react';

/**
 * Subscriber vs Free site-feature comparison matrix.
 * Sourced from Feature_Access_Paid_vs_Free_Matrix_v2.xlsx — Search.Inc product.
 *
 * Structure mirrors the spreadsheet:
 *   • Section header (icon + title + tag-line)
 *   • Per-row: feature label · subscriber pill · VS divider · free pill · free-user note
 *
 * Color tokens map directly to the workbook:
 *   green  → 00B050  (positive / "YES" / "CANNOT")
 *   red    → FF5050  (restrictive / "NO")
 *   yellow → FFFF00  (partial / capped)
 */

const STATUS_STYLE = {
  green: {
    pill: 'bg-emerald-500 text-emerald-950',
    glow: 'shadow-[0_0_18px_rgba(16,185,129,0.45)]',
  },
  red: {
    pill: 'bg-red-500 text-red-50',
    glow: 'shadow-[0_0_18px_rgba(239,68,68,0.45)]',
  },
  yellow: {
    pill: 'bg-yellow-400 text-yellow-950',
    glow: 'shadow-[0_0_18px_rgba(250,204,21,0.45)]',
  },
};

// ─────────────────────────────────────────────────────────────────────────
// Matrix data — Revenue Share leads (per user request), then 4 detail sections
// ─────────────────────────────────────────────────────────────────────────
const SECTIONS = [
  {
    id: 'revenue-share',
    title: 'Revenue Share',
    tag: 'Only one like it in the world.',
    Icon: Coins,
    accent: 'emerald',
    rows: [
      {
        feature:
          'As we grow so do our most engaged users. At 4% market share, almost $1 Billion will be shared amongst 80k users.',
        // Inline link rendered after the feature text — routes the user to
        // the full Revenue Share Terms screen in the Menu via the
        // onRevenueDetailsClick callback wired by the parent.
        featureLink: { label: 'Click here for details', target: 'revenue-terms' },
        sub: { val: 'YES', color: 'green' },
        free: { val: 'NO', color: 'red' },
        freeNote: 'Free version: view access only.',
      },
    ],
  },
  {
    id: 'data-privacy',
    title: 'Data / Privacy',
    tag: 'More robust data control than EU (GDPR) — "best in the world".',
    Icon: Lock,
    accent: 'cyan',
    rows: [
      {
        feature: 'Username & actual name cannot be connected — complete anonymity',
        sub: { val: 'YES', color: 'green' },
        free: { val: 'Partial', color: 'yellow' },
        freeNote: 'Free user can be identified through various methods (e.g. username connected to device).',
      },
      {
        feature: 'Control Centre — Username controls data authority',
        sub: { val: 'YES', color: 'green' },
        free: { val: "NO (can see, can't action)", color: 'red' },
        freeNote: 'Accuracy Controls are set to "on" and cannot be changed.',
      },
      {
        feature: 'Data attached to your Username only — not sellable',
        sub: { val: 'CANNOT', color: 'green' },
        free: { val: 'CANNOT', color: 'green' },
        freeNote: 'This is attached to your Username only and is not sellable.',
      },
      {
        feature: 'Cannot be provided to 3rd parties if legally requested',
        sub: { val: 'CANNOT', color: 'green' },
        free: { val: 'CANNOT', color: 'green' },
        freeNote: 'Cannot provide to 3rd parties if legally requested.',
      },
      {
        feature: 'Encrypted at rest and in flight (E2E)',
        sub: { val: 'YES', color: 'green' },
        free: { val: 'YES', color: 'green' },
        freeNote: 'Encrypted at rest and in flight (E2E).',
      },
      {
        feature: 'Username Data control',
        sub: { val: 'YES', color: 'green' },
        free: { val: "NO (can see, can't action)", color: 'red' },
        freeNote: 'Will be tracked and recorded for the free version.',
      },
      {
        feature: 'Location Data control',
        sub: { val: 'YES', color: 'green' },
        free: { val: "NO (can see, can't action)", color: 'red' },
        freeNote: 'Will be tracked and recorded for the free version.',
      },
      {
        feature: 'Social Media Data control',
        sub: { val: 'YES', color: 'green' },
        free: { val: "NO (can see, can't action)", color: 'red' },
        freeNote: 'Will be tracked and recorded for the free version.',
      },
      {
        feature: 'Media Rich Data control',
        sub: { val: 'YES', color: 'green' },
        free: { val: "NO (can see, can't action)", color: 'red' },
        freeNote: 'Will be tracked and recorded for the free version.',
      },
      {
        feature: 'Session History control',
        sub: { val: 'YES', color: 'green' },
        free: { val: 'YES', color: 'green' },
        freeNote: '',
      },
      {
        feature: 'Nuclear Option — delete everything we possibly can',
        sub: { val: 'YES', color: 'green' },
        free: { val: "NO (can see, can't action)", color: 'red' },
        freeNote: '',
      },
      {
        feature: 'Cookies / trackers blocked from other sites',
        sub: { val: 'YES', color: 'green' },
        free: { val: 'YES', color: 'green' },
        freeNote: '',
      },
      {
        feature: 'Safety View functionality',
        sub: { val: 'YES', color: 'green' },
        free: { val: 'NO', color: 'red' },
        freeNote: '',
      },
      {
        feature: 'Knowledge Graph Control',
        sub: { val: 'YES', color: 'green' },
        free: { val: 'NO', color: 'red' },
        freeNote: '',
      },
      {
        feature: 'Subscription Control access',
        sub: { val: 'YES', color: 'green' },
        free: { val: 'YES', color: 'green' },
        freeNote: 'Subscription Control access.',
      },
    ],
  },
  {
    id: 'user-experience',
    title: 'User Experience · Control-Centric Design',
    tag: 'Most unique UX / UI in the industry.',
    Icon: Sparkles,
    accent: 'fuchsia',
    rows: [
      {
        feature: 'Proprietary AI · intent-based query capability',
        sub: { val: 'YES', color: 'green' },
        free: { val: 'YES', color: 'green' },
        freeNote: 'Proprietary AI · intent-based query capability.',
      },
      {
        feature: 'Control Centre — full functionality',
        sub: { val: 'YES', color: 'green' },
        free: { val: 'NO', color: 'red' },
        freeNote: 'Free users can see everything but cannot change control settings.',
      },
      {
        feature: 'Change returned-result presentation',
        sub: { val: 'YES', color: 'green' },
        free: { val: 'YES', color: 'green' },
        freeNote: 'Change returned-result presentation.',
      },
      {
        feature: 'Returned results unique + RLHF (reinforced human feedback loop)',
        sub: { val: 'YES', color: 'green' },
        free: { val: 'YES', color: 'green' },
        freeNote: 'Returned results unique + RLHF.',
      },
      {
        feature: 'Knowledge Graph + Agentic Tool access',
        sub: { val: 'YES', color: 'green' },
        free: { val: 'NO', color: 'red' },
        freeNote: '',
      },
      {
        feature: 'Available at launch: Voice to Agent functionality',
        sub: { val: 'YES', color: 'green' },
        free: { val: 'NO', color: 'red' },
        freeNote: '',
      },
    ],
  },
  {
    id: 'accuracy',
    title: 'Accuracy · Intent-Based Search',
    tag: 'Best in class — 10% or lower error rate.',
    Icon: Target,
    accent: 'amber',
    rows: [
      {
        feature: 'Searching proprietary AI agent / engine',
        sub: { val: 'YES', color: 'green' },
        free: { val: 'YES', color: 'green' },
        freeNote: '',
      },
      {
        feature: 'No ad bias in algorithm',
        sub: { val: 'YES', color: 'green' },
        free: { val: 'YES', color: 'green' },
        freeNote: 'No ad bias in algorithm.',
      },
      {
        feature: 'Data (+ / −) signals impact accuracy',
        sub: { val: 'YES', color: 'green' },
        free: { val: 'YES', color: 'green' },
        freeNote: 'Free users cannot change from "on".',
      },
      {
        feature: 'Returned results don\'t track our users (cookies / trackers)',
        sub: { val: 'YES', color: 'green' },
        free: { val: 'YES', color: 'green' },
        freeNote: 'Returned results don\'t track our users.',
      },
      {
        feature: '# of searches per week',
        sub: { val: 'TBD', color: 'green' },
        free: { val: '20 / week', color: 'yellow' },
        freeNote: '',
      },
    ],
  },
  {
    id: 'ads',
    title: 'Ads',
    tag: 'A new way to engage with ads — introduced at a later time. Ads will not affect returned results in any way.',
    Icon: Megaphone,
    accent: 'sky',
    rows: [
      {
        feature: 'Ads (introduced later) — never affect returned results',
        sub: { val: 'Yes (timing TBD)', color: 'green' },
        free: { val: 'Yes (timing TBD)', color: 'green' },
        freeNote: 'Ads will not affect returned results in any way.',
      },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────

const Pill = ({ status, isMobile }) => {
  const style = STATUS_STYLE[status.color] || STATUS_STYLE.green;
  return (
    <span
      className={`inline-flex items-center justify-center px-3 py-1.5 rounded-full font-bold tracking-wide ${style.pill} ${style.glow} ${isMobile ? 'text-[11px]' : 'text-xs'}`}
      data-testid={`svf-pill-${status.color}`}
    >
      {status.val}
    </span>
  );
};

const Row = ({ row, isMobile, idx, onLinkClick }) => {
  return (
    <div
      className={`grid items-center ${isMobile ? 'gap-2 py-3' : 'gap-3 py-3.5'} border-b border-cyan-500/10 last:border-b-0`}
      style={{
        gridTemplateColumns: isMobile
          ? '1fr'
          : 'minmax(0, 1.5fr) 130px 36px 130px minmax(0, 1.4fr)',
      }}
      data-testid={`svf-row-${idx}`}
    >
      {/* Feature label */}
      <div className={`text-gray-200 leading-snug ${isMobile ? 'text-[13px] font-medium' : 'text-sm font-medium'}`}>
        {row.feature}
        {row.featureLink && (
          <>
            {' '}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (onLinkClick) onLinkClick(row.featureLink.target);
              }}
              className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2 decoration-dotted decoration-cyan-400/50 transition-colors font-semibold"
              data-testid={`svf-row-${idx}-feature-link`}
            >
              {row.featureLink.label}
            </button>
          </>
        )}
      </div>

      {/* Subscriber pill */}
      <div className={`flex ${isMobile ? 'gap-2 items-center mt-1' : 'justify-center'}`}>
        {isMobile && <span className="text-[10px] uppercase tracking-wider text-cyan-300 font-semibold w-20">Subscriber</span>}
        <Pill status={row.sub} isMobile={isMobile} />
      </div>

      {/* VS divider */}
      {!isMobile && (
        <div className="flex justify-center">
          <span className="text-[10px] uppercase tracking-[0.2em] text-cyan-500/50 font-bold">vs</span>
        </div>
      )}

      {/* Free pill */}
      <div className={`flex ${isMobile ? 'gap-2 items-center' : 'justify-center'}`}>
        {isMobile && <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold w-20">Free</span>}
        <Pill status={row.free} isMobile={isMobile} />
      </div>

      {/* Free-user note */}
      <div
        className={`text-gray-400 leading-snug ${isMobile ? 'text-[11px] mt-1 italic' : 'text-xs italic'}`}
      >
        {row.freeNote || (isMobile ? '' : '—')}
      </div>
    </div>
  );
};

const Section = ({ section, isMobile, onLinkClick }) => {
  const { Icon, title, tag, rows, id, accent } = section;
  // Map accent → tailwind colour stops for the section ring/icon
  const accents = {
    cyan: { text: 'text-cyan-300', ring: 'rgba(34,211,238,0.20)', glow: 'rgba(34,211,238,0.10)' },
    fuchsia: { text: 'text-fuchsia-300', ring: 'rgba(232,121,249,0.20)', glow: 'rgba(232,121,249,0.10)' },
    amber: { text: 'text-amber-300', ring: 'rgba(252,211,77,0.20)', glow: 'rgba(252,211,77,0.10)' },
    sky: { text: 'text-sky-300', ring: 'rgba(125,211,252,0.20)', glow: 'rgba(125,211,252,0.10)' },
    emerald: { text: 'text-emerald-300', ring: 'rgba(110,231,183,0.22)', glow: 'rgba(110,231,183,0.12)' },
  }[accent] || { text: 'text-cyan-300', ring: 'rgba(34,211,238,0.20)', glow: 'rgba(34,211,238,0.10)' };

  return (
    <section
      className="relative rounded-2xl overflow-hidden"
      style={{
        background:
          'linear-gradient(160deg, rgba(6,12,24,0.85) 0%, rgba(10,18,32,0.85) 100%)',
        border: `1px solid ${accents.ring}`,
        boxShadow: `0 0 0 1px ${accents.glow}, 0 12px 32px rgba(0,0,0,0.35)`,
      }}
      data-testid={`svf-section-${id}`}
    >
      {/* Section header */}
      <div
        className={`relative flex items-start gap-3 ${isMobile ? 'p-4' : 'px-6 py-5'}`}
        style={{
          background:
            `linear-gradient(180deg, ${accents.glow} 0%, transparent 100%)`,
          borderBottom: `1px solid ${accents.ring}`,
        }}
      >
        <Icon className={`flex-shrink-0 ${accents.text} ${isMobile ? 'w-5 h-5 mt-0.5' : 'w-6 h-6 mt-1'}`} />
        <div>
          <h3 className={`${isMobile ? 'text-base' : 'text-xl'} font-bold text-white leading-tight`}>
            {title}
          </h3>
          <p className={`${isMobile ? 'text-xs' : 'text-sm'} ${accents.text} font-light mt-1`}>
            {tag}
          </p>
        </div>
      </div>

      {/* Column headers (desktop only) */}
      {!isMobile && (
        <div
          className="grid items-center gap-3 px-6 pt-4 pb-2 text-[10px] uppercase tracking-[0.18em] font-bold text-gray-500"
          style={{ gridTemplateColumns: 'minmax(0, 1.5fr) 130px 36px 130px minmax(0, 1.4fr)' }}
        >
          <span>Feature</span>
          <span className="text-center text-cyan-400">Subscriber</span>
          <span></span>
          <span className="text-center text-gray-400">Free</span>
          <span>Free-user note</span>
        </div>
      )}

      {/* Rows */}
      <div className={isMobile ? 'px-4 pb-2' : 'px-6 pb-3'}>
        {rows.map((row, idx) => (
          <Row key={idx} row={row} idx={idx} isMobile={isMobile} onLinkClick={onLinkClick} />
        ))}
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Main exported panel
// ─────────────────────────────────────────────────────────────────────────

export const SubscriberVsFreeMatrix = ({ onBack, isMobile = false, onNavigate }) => {
  return (
    <div
      className={`relative z-10 w-full ${isMobile ? 'mx-2 max-h-[95vh]' : 'max-w-6xl mx-4 max-h-[90vh]'} rounded-2xl flex flex-col overflow-hidden`}
      style={{
        background:
          'linear-gradient(135deg, rgba(10, 15, 30, 0.98) 0%, rgba(20, 30, 50, 0.98) 100%)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(100, 200, 255, 0.15)',
      }}
      data-testid="subscriber-vs-free-panel"
    >
      <button
        onClick={onBack}
        className="absolute top-5 right-6 text-gray-300 hover:text-white transition-colors text-xl z-20"
        data-testid="subscriber-vs-free-back-btn"
      >
        &#x21A9;
      </button>

      <div className="overflow-y-auto policy-content">
        {/* Hero header */}
        <div className={`relative ${isMobile ? 'px-4 pt-6 pb-4' : 'px-8 pt-10 pb-6'} text-center overflow-hidden`}>
          <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/10 via-transparent to-transparent"></div>
          {!isMobile && (
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-cyan-500/15 rounded-full blur-3xl"></div>
          )}
          <div className="relative">
            <h1 className={`${isMobile ? 'text-2xl' : 'text-5xl'} font-bold mb-3 text-cyan-400`}>
              Subscriber <span className="text-cyan-300/80 font-semibold">vs</span> Free Site Comparison
            </h1>
          </div>
        </div>

        {/* Subscription intro band */}
        <div className={`${isMobile ? 'px-3 pb-4' : 'px-8 pb-6'}`}>
          <div
            className={`relative rounded-2xl overflow-hidden ${isMobile ? 'p-4' : 'p-6'} flex flex-col md:flex-row items-stretch md:items-center gap-4`}
            style={{
              background:
                'linear-gradient(135deg, rgba(0,180,220,0.18) 0%, rgba(8,30,42,0.95) 60%, rgba(15,25,45,0.95) 100%)',
              border: '1px solid rgba(34,211,238,0.30)',
              boxShadow:
                '0 0 0 1px rgba(34,211,238,0.15), 0 12px 32px rgba(0,0,0,0.4), 0 0 28px rgba(34,211,238,0.18)',
            }}
            data-testid="svf-subscription-band"
          >
            {/* Price tile */}
            <div
              className={`${isMobile ? 'w-full' : 'w-56 flex-shrink-0'} rounded-xl text-center`}
              style={{
                background:
                  'linear-gradient(180deg, rgba(34,211,238,0.20) 0%, rgba(34,211,238,0.05) 100%)',
                border: '1px solid rgba(34,211,238,0.40)',
                padding: isMobile ? '14px' : '20px',
              }}
            >
              <p className={`${isMobile ? 'text-xs' : 'text-xs'} uppercase tracking-[0.18em] text-cyan-300/80 font-bold`}>
                Subscription
              </p>
              <p className={`${isMobile ? 'text-2xl' : 'text-4xl'} font-bold text-white mt-1`}>
                $35 <span className="text-lg text-cyan-300">CAD</span>
              </p>
              <p className="text-[11px] uppercase tracking-wider text-cyan-300/70 mt-0.5">
                full feature access
              </p>
            </div>

            {/* Vs blurb */}
            <div className="flex-1">
              <div className={`flex flex-wrap items-center gap-3 ${isMobile ? 'mt-3' : 'mt-6'}`}>
                <Pill status={{ val: 'Subscriber: YES', color: 'green' }} isMobile={isMobile} />
                <span className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold">vs</span>
                <Pill status={{ val: 'Free: NO', color: 'red' }} isMobile={isMobile} />
                <p className={`${isMobile ? 'text-sm w-full mt-2' : 'text-base flex-1 ml-2'} text-gray-300 leading-relaxed`}>
                  Some functionality is not available and there is a limit of{' '}
                  <span className="text-yellow-300 font-semibold">20 searches per week</span>.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className={`${isMobile ? 'px-3 pb-4' : 'px-8 pb-4'} flex flex-wrap justify-center gap-4 ${isMobile ? 'text-[11px]' : 'text-xs'}`}>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.55)]"></span>
            <span className="text-emerald-300">YES / CANNOT (good)</span>
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-yellow-400 shadow-[0_0_10px_rgba(250,204,21,0.55)]"></span>
            <span className="text-yellow-300">Partial / capped</span>
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.55)]"></span>
            <span className="text-red-300">NO / restricted</span>
          </span>
        </div>

        {/* Sections */}
        <div className={`${isMobile ? 'px-3 pb-6 space-y-4' : 'px-8 pb-10 space-y-6'}`}>
          {SECTIONS.map((section) => (
            <Section key={section.id} section={section} isMobile={isMobile} onLinkClick={onNavigate} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SubscriberVsFreeMatrix;
